import cms from './cms';

export interface CancellationParams {
  subscriptionId: string;
  uid: string;
  userImpersonationId: string;
  noRefund: boolean;
  customRefundAmount?: number;
  isAutomaticRefund?: boolean;
}

export interface CancellationResponse {
  success: boolean;
  message: string;
  data?: any;
}

export interface PreviewCancellationParams {
  subscriptionId: string;
  uid: string;
  userImpersonationId: string;
}

export interface RefundCalculation {
  will_process_refund: boolean;
  refund_amount: number;
  paid_amount_from_payments: number;
  subscription_amount: number;
  has_coupon_discount: boolean;
  calculation_method: string;
  country_id: number;
  no_refund_requested: boolean;
}

export interface SubscriptionPreview {
  id: string;
  amount: number;
  date_init: string;
  date_expire: string;
  periocity: string;
}

export interface PreviewCancellationResponse {
  success: boolean;
  message?: string;
  preview?: boolean;
  subscription?: SubscriptionPreview;
  refund_calculation?: RefundCalculation;
}

/**
 * Obtiene una vista previa de la cancelación con el cálculo del reembolso desde el servidor
 * @param params - Parámetros de la vista previa
 * @returns Promise con la respuesta de la vista previa
 */
export async function previewCancellation(
  params: PreviewCancellationParams
): Promise<PreviewCancellationResponse> {
  try {
    const { subscriptionId, uid, userImpersonationId } = params;
    
    // Construir la URL base
    const baseUrl = `/v2/user-subscriptions/${subscriptionId}`;
    
    // Construir los parámetros de consulta
    const queryParams = new URLSearchParams({
      uid,
      userImpersonationId,
      noRefund: 'false',
      process: 'false', // Esto nos da la vista previa sin procesar la cancelación
    });

    // Realizar la petición DELETE para obtener la vista previa
    const response = await cms.delete(`${baseUrl}?${queryParams}`);

    return {
      success: true,
      preview: response.data?.preview,
      subscription: response.data?.subscription,
      refund_calculation: response.data?.refund_calculation,
      message: response.data?.message,
    };
  } catch (error: any) {
    console.error('Error al obtener vista previa de cancelación:', error);
    
    return {
      success: false,
      message: 'Error al obtener la información del reembolso. Por favor intenta nuevamente.',
    };
  }
}

/**
 * Cancela una suscripción con o sin reembolso
 * @param params - Parámetros de cancelación
 * @returns Promise con la respuesta de la cancelación
 */
export async function cancelSubscription(
  params: CancellationParams
): Promise<CancellationResponse> {
  try {
    const { subscriptionId, uid, userImpersonationId, noRefund, customRefundAmount, isAutomaticRefund } = params;
    
    // Construir la URL base
    const baseUrl = `/v2/user-subscriptions/${subscriptionId}`;
    
    // Construir los parámetros de consulta
    const queryParams = new URLSearchParams({
      uid,
      userImpersonationId,
      noRefund: noRefund.toString(),
    });

    // Agregar monto de reembolso personalizado solo si NO es automático
    // Si es automático, el backend calculará el monto automáticamente
    if (!noRefund && !isAutomaticRefund && customRefundAmount !== undefined) {
      queryParams.append('customRefundAmount', customRefundAmount.toString());
    }
    // process=true para ejecutar la cancelación real
    queryParams.append('process', 'true');

    // Realizar la petición DELETE
    const response = await cms.delete(`${baseUrl}?${queryParams}`);

    return {
      success: true,
      message: noRefund 
        ? 'Suscripción cancelada sin reembolso exitosamente'
        : `Reembolso de $${customRefundAmount} procesado exitosamente`,
      data: response.data,
    };
  } catch (error: any) {
    console.error('Error al cancelar suscripción:', error);
    
    // Usar mensaje genérico para mejor UX
    const errorMessage = 'Error al cancelar la suscripción. Por favor intenta nuevamente.';

    return {
      success: false,
      message: errorMessage,
    };
  }
}

/**
 * Cancela una suscripción sin reembolso
 * @param subscriptionId - ID de la suscripción
 * @param uid - ID del usuario
 * @param userImpersonationId - ID de impersonación del usuario
 * @returns Promise con la respuesta de la cancelación
 */
export async function cancelSubscriptionWithoutRefund(
  subscriptionId: string,
  uid: string,
  userImpersonationId: string
): Promise<CancellationResponse> {
  return cancelSubscription({
    subscriptionId,
    uid,
    userImpersonationId,
    noRefund: true,
  });
}

/**
 * Cancela una suscripción con reembolso personalizado
 * @param subscriptionId - ID de la suscripción
 * @param uid - ID del usuario
 * @param userImpersonationId - ID de impersonación del usuario
 * @param customRefundAmount - Monto del reembolso personalizado
 * @returns Promise con la respuesta de la cancelación
 */
export async function cancelSubscriptionWithCustomRefund(
  subscriptionId: string,
  uid: string,
  userImpersonationId: string,
  customRefundAmount: number,
  isAutomaticRefund = false
): Promise<CancellationResponse> {
  return cancelSubscription({
    subscriptionId,
    uid,
    userImpersonationId,
    noRefund: false,
    customRefundAmount,
    isAutomaticRefund,
  });
}
